import React, { useState, useContext, useMemo } from 'react';
import PropTypes from 'prop-types';
import './css/ComparisonTable.css';
import { ErrorContext } from '../context/ErrorContext';
import { AppButton, DataTableShell } from '../components/ui';

const statKeys = [
  { key: 'battlePower', label: 'Top 100 Total Battle Power' },
  { key: 'officerPower', label: 'Top 100 Total Officer Power' },
  { key: 'titanPower', label: 'Top 100 Total Titan Power' },
  { key: 'warplanePower', label: 'Top 100 Total Warplane Power' },
  { key: 'islandScore', label: 'Top 100 Total Island Score' },
  { key: 'antiquesScore', label: 'Top 100 Total Antiques Score' },
  { key: 'totalMerit', label: 'Top 100 Total Merit' }
];

const ComparisonTable = ({
  comparisonData,
  showLocationColumn = true,
  showBloatColumn = true
}) => {
  const { showError } = useContext(ErrorContext);
  const [sortConfig, setSortConfig] = useState({
    key: 'ranks.battlePowerRank',
    direction: 'ascending'
  });

  const nations = comparisonData?.nations ?? [];

  const sortedCommanders = useMemo(() => {
    const { commandersWithRanks = [] } = comparisonData ?? {};
    return [...commandersWithRanks].sort((a, b) => {
      const aValue = sortConfig.key
        .split('.')
        .reduce((acc, part) => (acc ? acc[part] : undefined), a);
      const bValue = sortConfig.key
        .split('.')
        .reduce((acc, part) => (acc ? acc[part] : undefined), b);

      if (aValue === bValue) return 0;
      if (aValue === undefined || aValue === null) return 1;
      if (bValue === undefined || bValue === null) return -1;
      if (aValue < bValue) {
        return sortConfig.direction === 'ascending' ? -1 : 1;
      }
      if (aValue > bValue) {
        return sortConfig.direction === 'ascending' ? 1 : -1;
      }
      return 0;
    });
  }, [comparisonData, sortConfig]);

  const formatValue = (value) =>
    value !== undefined && value !== null
      ? Number(value).toLocaleString()
      : '-';

  const formatDate = (value) => {
    if (!value) return '-';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '-';
    return date.toLocaleDateString();
  };

  const copyToClipboard = (commander) => {
    const acronym = commander.alliance?.acronym ?? 'N/A';
    const location = commander.location ?? 'UNKNOWN';
    const locationString = `I found (${acronym}) ${commander.name}, Location: ${location}, City Level: ${commander.cityLevel}, Battle Power: ${formatValue(
      commander.battlePower
    )}, BP - Rank Δ: ${commander.ranks?.bloat?.toFixed?.(2) ?? 'N/A'}`;
    navigator.clipboard
      .writeText(locationString)
      .then(() => {
        showError('Location info copied to clipboard!');
      })
      .catch((err) => {
        console.error('Failed to copy text: ', err);
      });
  };

  const handleSort = (key) => {
    setSortConfig((current) => {
      if (current.key === key) {
        return {
          key,
          direction: current.direction === 'ascending' ? 'descending' : 'ascending'
        };
      }
      return { key, direction: 'ascending' };
    });
  };

  const getSortIndicator = (key) => {
    if (sortConfig.key !== key) return '';
    return sortConfig.direction === 'ascending' ? ' ↑' : ' ↓';
  };

  if (!comparisonData) {
    return null;
  }

  return (
    <DataTableShell title="Nation Comparison" className="comparison-table">
      <table className="comparison-summary-table">
        <thead>
          <tr>
            <th>Stat</th>
            {nations.map((nation) => (
              <th key={nation.id}>{nation.name ?? nation.number}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {statKeys.map(({ key, label }) => (
            <tr key={key}>
              <td>{label}</td>
              {nations.map((nation) => (
                <td key={`${key}-${nation.id}`}>
                  {formatValue(
                    comparisonData?.totals?.[key]?.[nation.id] ?? null
                  )}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>

      <h3>Commanders</h3>
      <table className="comparison-commander-list">
        <thead>
          <tr>
            <th onClick={() => handleSort('updatedAt')}>
              Updated {getSortIndicator('updatedAt')}
            </th>
            <th onClick={() => handleSort('nation.number')}>
              Nation {getSortIndicator('nation.number')}
            </th>
            <th onClick={() => handleSort('alliance.acronym')}>
              Alliance {getSortIndicator('alliance.acronym')}
            </th>
            <th onClick={() => handleSort('name')}>
              Name {getSortIndicator('name')}
            </th>
            {showLocationColumn && (
              <th onClick={() => handleSort('location')}>
                Location {getSortIndicator('location')}
              </th>
            )}
            <th onClick={() => handleSort('cityLevel')}>
              City Level {getSortIndicator('cityLevel')}
            </th>
            <th onClick={() => handleSort('ranks.battlePowerRank')}>
              BP Rank {getSortIndicator('ranks.battlePowerRank')}
            </th>
            <th onClick={() => handleSort('battlePower')}>
              Battle Power {getSortIndicator('battlePower')}
            </th>
            <th onClick={() => handleSort('rankingData.individualReputation')}>
              Rep {getSortIndicator('rankingData.individualReputation')}
            </th>
            <th onClick={() => handleSort('rankingData.kills')}>
              Kills {getSortIndicator('rankingData.kills')}
            </th>
            <th onClick={() => handleSort('rankingData.losses')}>
              Losses {getSortIndicator('rankingData.losses')}
            </th>
            <th onClick={() => handleSort('ranks.officerRank')}>
              Officer Rank {getSortIndicator('ranks.officerRank')}
            </th>
            <th onClick={() => handleSort('rankingData.officer.power')}>
              Officer Power {getSortIndicator('rankingData.officer.power')}
            </th>
            <th onClick={() => handleSort('ranks.titanRank')}>
              Titan Rank {getSortIndicator('ranks.titanRank')}
            </th>
            <th onClick={() => handleSort('rankingData.titan.power')}>
              Titan Power {getSortIndicator('rankingData.titan.power')}
            </th>
            <th onClick={() => handleSort('ranks.warplaneRank')}>
              Warplane Rank {getSortIndicator('ranks.warplaneRank')}
            </th>
            <th onClick={() => handleSort('rankingData.warplane.power')}>
              Warplane Power {getSortIndicator('rankingData.warplane.power')}
            </th>
            <th onClick={() => handleSort('ranks.islandRank')}>
              Island Rank {getSortIndicator('ranks.islandRank')}
            </th>
            <th onClick={() => handleSort('rankingData.island.score')}>
              Island Score {getSortIndicator('rankingData.island.score')}
            </th>
            <th onClick={() => handleSort('ranks.antiquesRank')}>
              Antiques Rank {getSortIndicator('ranks.antiquesRank')}
            </th>
            <th onClick={() => handleSort('rankingData.antiques.score')}>
              Antiques Score {getSortIndicator('rankingData.antiques.score')}
            </th>
            <th onClick={() => handleSort('ranks.meritRank')}>
              Merit Rank {getSortIndicator('ranks.meritRank')}
            </th>
            <th onClick={() => handleSort('totalMerit')}>
              Total Merit {getSortIndicator('totalMerit')}
            </th>
            <th onClick={() => handleSort('ranks.blendedRank')}>
              Blended Rank {getSortIndicator('ranks.blendedRank')}
            </th>
            {showBloatColumn && (
              <th onClick={() => handleSort('ranks.bloat')}>
                BP - Rank Δ {getSortIndicator('ranks.bloat')}
              </th>
            )}
          </tr>
        </thead>
        <tbody>
          {sortedCommanders.map((commander) => (
            <tr key={commander._id}>
              <td>{formatDate(commander.updatedAt)}</td>
              <td>{commander.nation?.number ?? '-'}</td>
              <td>{commander.alliance?.acronym ?? '-'}</td>
              <td>{commander.name}</td>
              {showLocationColumn && (
                <td>
                  <AppButton
                    type="button"
                    kind="secondary"
                    onClick={() => copyToClipboard(commander)}
                    className="comparison-copy-button"
                  >
                    {commander.location ?? 'N/A'}
                  </AppButton>
                </td>
              )}
              <td>{formatValue(commander.cityLevel)}</td>
              <td>{formatValue(commander.ranks?.battlePowerRank)}</td>
              <td>{formatValue(commander.battlePower)}</td>
              <td>{formatValue(commander.rankingData?.individualReputation)}</td>
              <td>{formatValue(commander.rankingData?.kills)}</td>
              <td>{formatValue(commander.rankingData?.losses)}</td>
              <td>{formatValue(commander.ranks?.officerRank)}</td>
              <td>{formatValue(commander.rankingData?.officer?.power)}</td>
              <td>{formatValue(commander.ranks?.titanRank)}</td>
              <td>{formatValue(commander.rankingData?.titan?.power)}</td>
              <td>{formatValue(commander.ranks?.warplaneRank)}</td>
              <td>{formatValue(commander.rankingData?.warplane?.power)}</td>
              <td>{formatValue(commander.ranks?.islandRank)}</td>
              <td>{formatValue(commander.rankingData?.island?.score)}</td>
              <td>{formatValue(commander.ranks?.antiquesRank)}</td>
              <td>{formatValue(commander.rankingData?.antiques?.score)}</td>
              <td>{formatValue(commander.ranks?.meritRank)}</td>
              <td>{formatValue(commander.totalMerit)}</td>
              <td>{formatValue(commander.ranks?.blendedRank)}</td>
              {showBloatColumn && (
                <td>{formatValue(commander.ranks?.bloat)}</td>
              )}
            </tr>
          ))}
        </tbody>
      </table>
    </DataTableShell>
  );
};

ComparisonTable.propTypes = {
  comparisonData: PropTypes.shape({
    nations: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.string.isRequired,
        name: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        number: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
      })
    ),
    totals: PropTypes.object,
    commandersWithRanks: PropTypes.arrayOf(PropTypes.object)
  }),
  showLocationColumn: PropTypes.bool,
  showBloatColumn: PropTypes.bool
};

ComparisonTable.defaultProps = {
  comparisonData: null,
  showLocationColumn: true,
  showBloatColumn: true
};

export default ComparisonTable;
